import React from 'react';
import { useNavigate } from 'react-router-dom';

const TeacherDashboard = () => {
  const navigate = useNavigate();

  const features = [
    {
      title: "🎵 TTS 스크립트 생성",
      description: "AI 기반 팟캐스트 스크립트 생성 및 음성 변환",
      path: "/teacher/tts",
      icon: "🎙️",
      color: "from-blue-600 to-cyan-600"
    },
    {
      title: "📝 학습 콘텐츠 관리",
      description: "AI 요약 내용 수정, 삭제, 품질 관리",
      path: "/teacher/content",
      icon: "✏️",
      color: "from-green-600 to-teal-600"
    },
    {
      title: "🎤 면접 질문 관리",
      description: "면접 질문 추가, 수정, 난이도 조절",
      path: "/teacher/interview",
      icon: "📋",
      color: "from-purple-600 to-indigo-600"
    },
    {
      title: "📊 학습 분석",
      description: "학생들의 학습 패턴 및 성취도 분석",
      path: "/teacher/analytics",
      icon: "📈",
      color: "from-orange-600 to-red-600"
    }
  ];

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* 헤더 */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-4">
            🎓 교사용 관리 패널
          </h1>
          <p className="text-gray-600 text-xl">
            NCS 학습 콘텐츠를 효율적으로 관리하고 분석하세요
          </p>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">총 모듈</p>
                <p className="text-3xl font-bold text-blue-600">13개</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                📚
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">AI 요약</p>
                <p className="text-3xl font-bold text-green-600">150+</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                🧠
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">면접 질문</p>
                <p className="text-3xl font-bold text-purple-600">80+</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                🎤
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">TTS 파일</p>
                <p className="text-3xl font-bold text-orange-600">25+</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                🎵
              </div>
            </div>
          </div>
        </div>

        {/* 기능 카드들 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              onClick={() => navigate(feature.path)}
              className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer"
            >
              <div className={`h-32 bg-gradient-to-r ${feature.color} rounded-t-2xl flex items-center justify-center`}>
                <span className="text-6xl">{feature.icon}</span>
              </div>

              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-lg leading-relaxed mb-6">
                  {feature.description}
                </p>

                <div className="flex items-center justify-between">
                  <div className={`px-4 py-2 bg-gradient-to-r ${feature.color} text-white rounded-lg text-sm font-semibold`}>
                    바로가기
                  </div>
                  <div className="text-gray-400">
                    →
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 하단 정보 */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">
              💡 교사용 패널 안내
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
              <div>
                <h4 className="font-semibold text-blue-600 mb-2">🎯 효율적 관리</h4>
                <p className="text-gray-600 text-sm">
                  모든 학습 콘텐츠를 한 곳에서 통합 관리하고 품질을 향상시킬 수 있습니다.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-green-600 mb-2">📊 실시간 분석</h4>
                <p className="text-gray-600 text-sm">
                  학생들의 학습 패턴을 실시간으로 분석하여 맞춤형 교육을 제공합니다.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-purple-600 mb-2">🚀 AI 활용</h4>
                <p className="text-gray-600 text-sm">
                  최신 AI 기술을 활용하여 고품질의 학습 자료를 자동으로 생성합니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherDashboard;